import React, { useMemo } from 'react';
import { useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { NamedLink, ResponsiveImage } from '../../components';
import { createSlug } from '../../util/urlHelpers';

import css from './CompareTable.module.css';

const formatSpecKey = key =>
  `${key}`
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, l => l.toUpperCase())
    .replace(/\b(wi-fi|wifi)\b/gi, 'Wi-Fi')
    .replace(/\b(usb|hdmi|rj45|ip)\b/gi, l => l.toUpperCase())
    .replace(/\b(ghz|mhz|mb|gb|kb|tb)\b/gi, l => l.toUpperCase())
    .replace(/\b(ieee|wpa|wpa2|wpa3|mqtt|http|https|coap)\b/gi, l => l.toUpperCase());

const parseSpecs = specs => {
  if (!specs) return null;
  if (typeof specs === 'object') return specs;
  if (typeof specs === 'string') {
    try {
      return JSON.parse(specs);
    } catch (e) {
      return null;
    }
  }
  return null;
};

const flattenSpecs = (specs, prefix = '') => {
  if (!specs || typeof specs !== 'object') return {};
  return Object.entries(specs).reduce((acc, [key, value]) => {
    const label = prefix ? `${prefix} / ${formatSpecKey(key)}` : formatSpecKey(key);
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return { ...acc, ...flattenSpecs(value, label) };
    }
    return { ...acc, [label]: value };
  }, {});
};

const formatSpecValue = value => {
  if (value === null || typeof value === 'undefined') return '—';
  if (Array.isArray(value)) {
    return (
      <ul className={css.bulletList}>
        {value.map((item, index) => (
          <li key={`${item}-${index}`}>{String(item)}</li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  return String(value);
};

const normalizeDeviceTypeValue = value => {
  if (!value) return '';
  if (Array.isArray(value)) {
    return createSlug(`${value[0]}`);
  }
  return createSlug(`${value}`);
};

const thermostatDeviceTypes = new Set(['thermostat', 'thermostat-connecte']);

const CompareTable = props => {
  const { firstListing, secondListing } = props;
  const intl = useIntl();

  const specs = useMemo(() => {
    const specsA = flattenSpecs(parseSpecs(firstListing?.attributes?.publicData?.device_specific_specs));
    const specsB = flattenSpecs(parseSpecs(secondListing?.attributes?.publicData?.device_specific_specs));
    const specsAKeys = Object.keys(specsA);
    const orderedKeys = [
      ...specsAKeys,
      ...Object.keys(specsB).filter(key => !Object.prototype.hasOwnProperty.call(specsA, key)),
    ];
    return { specsA, specsB, keys: orderedKeys };
  }, [firstListing, secondListing]);

  if (!firstListing || !secondListing) {
    return null;
  }

  const priceA = firstListing.attributes?.price;
  const priceB = secondListing.attributes?.price;
  const shortA = firstListing.attributes?.publicData?.shortdescription || firstListing.attributes?.description;
  const shortB = secondListing.attributes?.publicData?.shortdescription || secondListing.attributes?.description;
  const imageA = firstListing.images && firstListing.images.length > 0 ? firstListing.images[0] : null;
  const imageB = secondListing.images && secondListing.images.length > 0 ? secondListing.images[0] : null;
  const variantsA = imageA
    ? Object.keys(imageA?.attributes?.variants || {}).filter(k => k.startsWith('listing-card'))
    : [];
  const variantsB = imageB
    ? Object.keys(imageB?.attributes?.variants || {}).filter(k => k.startsWith('listing-card'))
    : [];
  const renderCompareCta = listing => {
    const listingId = listing?.id?.uuid;
    if (!listingId) return null;
    const rawDeviceType =
      listing?.attributes?.publicData?.deviceType || listing?.attributes?.publicData?.device_type;
    const normalizedDeviceType = normalizeDeviceTypeValue(rawDeviceType);
    const ctaLabel = thermostatDeviceTypes.has(normalizedDeviceType)
      ? 'Voir ce thermostat'
      : 'Voir ce produit';
    const slug = createSlug(listing?.attributes?.title || '');

    return (
      <NamedLink
        name="ListingPage"
        params={{ id: listingId, slug }}
        className={css.compareCtaButton}
      >
        {ctaLabel}
      </NamedLink>
    );
  };

  return (
    <div className={css.root}>
      <h2 className={css.title}>Comparaison détaillée</h2>
      <table className={css.table}>
        <thead>
          <tr>
            <th className={css.headCell}>Nom</th>
            <th className={css.headCell}>
              <span className={css.productName}>{firstListing.attributes?.title}</span>
            </th>
            <th className={css.headCell}>
              <span className={css.productName}>{secondListing.attributes?.title}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={css.keyCell}>Référence fabricant</td>
            <td className={css.valueCell}>
              {firstListing.attributes?.publicData?.ref_manufacturer || '—'}
            </td>
            <td className={css.valueCell}>
              {secondListing.attributes?.publicData?.ref_manufacturer || '—'}
            </td>
          </tr>
          <tr>
            <td className={css.keyCell}>Image</td>
            <td className={`${css.valueCell} ${css.thumbnailCell}`}>
              {imageA ? (
                <div className={css.thumbnailWrapper}>
                  <ResponsiveImage
                    className={css.thumbnailImage}
                    alt={firstListing.attributes?.title || ''}
                    image={imageA}
                    variants={variantsA}
                      sizes="200px"
                  />
                </div>
              ) : (
                '—'
              )}
            </td>
            <td className={`${css.valueCell} ${css.thumbnailCell}`}>
              {imageB ? (
                <div className={css.thumbnailWrapper}>
                  <ResponsiveImage
                    className={css.thumbnailImage}
                    alt={secondListing.attributes?.title || ''}
                    image={imageB}
                    variants={variantsB}
                      sizes="200px"
                  />
                </div>
              ) : (
                '—'
              )}
            </td>
          </tr>
          <tr>
            <td className={css.keyCell}>Prix</td>
            <td className={css.valueCell}>{priceA ? formatMoney(intl, priceA) : '—'}</td>
            <td className={css.valueCell}>{priceB ? formatMoney(intl, priceB) : '—'}</td>
          </tr>
          <tr>
            <td className={css.keyCell}>Description courte</td>
            <td className={css.valueCell}>{shortA || '—'}</td>
            <td className={css.valueCell}>{shortB || '—'}</td>
          </tr>
          {specs.keys.map(key => (
            <tr key={key}>
              <td className={css.keyCell}>{key}</td>
              <td className={css.valueCell}>{formatSpecValue(specs.specsA[key])}</td>
              <td className={css.valueCell}>{formatSpecValue(specs.specsB[key])}</td>
            </tr>
          ))}
          <tr>
            <td className={css.keyCell} />
            <td className={`${css.valueCell} ${css.ctaCell}`}>
              {renderCompareCta(firstListing)}
            </td>
            <td className={`${css.valueCell} ${css.ctaCell}`}>
              {renderCompareCta(secondListing)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default CompareTable;
